import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Divider,
} from '@mui/material';
import { 
  ExpandMore, 
  ChevronRight 
} from '@mui/icons-material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import FlexBetween from 'components/FlexBetween';


/* ===== SAFE HELPERS ===== */
const safeEntries = (obj) => Object.entries(obj || {});
const safeNumber = v => (typeof v === 'number' ? v : 0);

/* ===== SAFE DEFAULT ===== */
const emptyStats = {
    byRankType: {},
    byPostBase: {},
    byBasePost: {},
    bySickPostBase: {},
    bySickBasePost: {},
};


const CrewStats = ({ data }) => {
    const stats = data ?? emptyStats;
    const { byPostBase, byBasePost } = stats;

    return (
        <Box display={"flex"} flexDirection={"column"} gap={2}>      
            {/* ===== POST → BASE TREE ===== */}        
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" color="primary.dark" gutterBottom>
                    Block Hours by Post → Base
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <SimpleTreeView
                    defaultExpandedItems={[]}
                    slots={{
                        expandIcon: ChevronRight,
                        collapseIcon: ExpandMore,
                    }}
                >
                    {safeEntries(byPostBase)?.map(([post, bases]) => {
                        const totalCrew = safeEntries(bases).reduce(
                            (sum, [, v]) => sum + safeNumber(v.totalCrew),
                            0
                        );
                        const totalBlockHours = safeEntries(bases).reduce(
                            (sum, [, v]) => sum + safeNumber(v.blockHours),
                            0
                        );
                        const blockHoursPerCrew = totalCrew > 0 ? totalBlockHours / totalCrew : 0;
                        
                            
                        return (
                            <TreeItem
                                key={post}
                                itemId={post}                            
                                label={
                                    <FlexBetween>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" width={36}>
                                                {post}: 
                                            </Typography>
                                            <Typography variant="body2" textAlign="right" minWidth={22}>
                                                {totalCrew}
                                            </Typography>   
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" width={20}>
                                                BH: 
                                            </Typography>
                                            <Typography variant="body2" textAlign="right" minWidth={50}>
                                                {totalBlockHours?.toFixed(1)}
                                            </Typography>   
                                        </Box>    
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" width={68}>
                                                BH / Crew: 
                                            </Typography>
                                            <Typography variant="body2" textAlign="right" minWidth={25}>
                                                {blockHoursPerCrew?.toFixed(1)}
                                            </Typography>   
                                        </Box>             
                                    </FlexBetween>
                                }
                            >
                                <Table size="small" sx={{ width: '100%' }}>
                                    {/* ===== TABLE HEADER ===== */}
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Base</TableCell>
                                            <TableCell align="right">Crew</TableCell>
                                            <TableCell align="right">BH</TableCell>
                                            <TableCell align="right">BH / Crew</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    {/* ===== TABLE BODY ===== */}
                                    <TableBody>
                                        {safeEntries(bases).map(([base, v]) => {
                                            return (
                                                <TableRow key={`${post}-${base}`}>
                                                    <TableCell>{base}</TableCell>
                                                    <TableCell align="right">
                                                        {safeNumber(v.totalCrew)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {safeNumber(v.blockHours).toFixed(1)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {safeNumber(v.blockHoursPerCrew).toFixed(1)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TreeItem>
                        )
                    })}
                </SimpleTreeView>
            </Paper>

            {/* ===== BASE → POST TREE ===== */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" color="primary.dark" gutterBottom>
                    Block Hours by Base → Post
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <SimpleTreeView
                    defaultExpandedItems={[]}
                    slots={{
                        expandIcon: ChevronRight,
                        collapseIcon: ExpandMore,
                    }}
                >
                    {safeEntries(byBasePost)?.map(([base, posts]) => {
                        const totalCrew = safeEntries(posts).reduce(
                            (sum, [, v]) => sum + safeNumber(v.totalCrew),
                            0
                        );
                        const totalBlockHours = safeEntries(posts).reduce(
                            (sum, [, v]) => sum + safeNumber(v.blockHours),
                            0
                        );
                        const blockHoursPerCrew = totalCrew > 0 ? totalBlockHours / totalCrew : 0;
                       

                        return (
                            <TreeItem
                                key={base}
                                itemId={base}
                                label={
                                    <FlexBetween>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" minWidth={36}>
                                                {base}: 
                                            </Typography>
                                            <Typography variant="body2" textAlign="right" minWidth={22}>
                                                {totalCrew}
                                            </Typography>   
                                        </Box>       
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" width={20}>
                                                BH: 
                                            </Typography>
                                            <Typography variant="body2" textAlign="right" minWidth={50}>
                                                {totalBlockHours?.toFixed(1)}
                                            </Typography>   
                                        </Box>          
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" width={68}>
                                                BH / Crew: 
                                            </Typography>
                                            <Typography variant="body2" textAlign="right" minWidth={25}>
                                                {blockHoursPerCrew.toFixed(1)}
                                            </Typography>   
                                        </Box>             
                                    </FlexBetween>
                                }
                            >
                                <Table size="small" sx={{ width: '100%' }}>
                                    {/* ===== TABLE HEADER ===== */}
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Post</TableCell>
                                            <TableCell align="right">Crew</TableCell>
                                            <TableCell align="right">BH</TableCell>
                                            <TableCell align="right">BH / Crew</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    {/* ===== TABLE BODY ===== */}
                                    <TableBody>
                                        {safeEntries(posts).map(([post, v]) => {
                                            return (
                                                <TableRow key={`${base}-${post}`}>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {post}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {safeNumber(v.totalCrew)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {safeNumber(v.blockHours).toFixed(1)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {safeNumber(v.blockHoursPerCrew).toFixed(1)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TreeItem>
                        )
                    })}
                </SimpleTreeView>
            </Paper>
        </Box>
    );
}

export default CrewStats;
